import { useState } from 'react';
import type { ExportFormat } from '@markbird/shared';
import { useStore } from '../store/useStore';
import type { ThemePreference } from '@markbird/shared';
import { useExport } from '../hooks/useExport';
import { useShare } from '../hooks/useShare';
import { useT } from '../i18n/useT';
import { AccountButton } from './AccountButton';
import { Icon } from './Icon';
import type { IconName } from './Icon';
import { Menu, MenuItem, MenuSeparator, anchorBelow } from './Menu';
import type { MenuAnchor } from './Menu';
import markMono from '../assets/mark-mono.svg?raw';

const THEME_CYCLE: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

const THEME_LABEL_KEY: Record<ThemePreference, string> = {
  system: 'theme.auto',
  light: 'theme.light',
  dark: 'theme.dark',
};

/** Recognizable theme icons so the control doesn't read as a generic mode switch. */
const THEME_ICON: Record<ThemePreference, IconName> = {
  system: 'monitor',
  light: 'sun',
  dark: 'moon',
};

const EXPORT_ITEMS: { fmt: ExportFormat; key: string }[] = [
  { fmt: 'md', key: 'export.md' },
  { fmt: 'html', key: 'export.html' },
  { fmt: 'pdf', key: 'export.pdf' },
  { fmt: 'docx', key: 'export.docx' },
  { fmt: 'slides', key: 'export.slides' },
];

export function Toolbar() {
  const title = useStore((s) => s.activeDoc.title);
  const setTitle = useStore((s) => s.setTitle);
  const viewMode = useStore((s) => s.viewMode);
  const toggleViewMode = useStore((s) => s.toggleViewMode);
  const switchPane = useStore((s) => s.switchPane);
  const setSwitchPane = useStore((s) => s.setSwitchPane);
  const theme = useStore((s) => s.theme);
  const setTheme = useStore((s) => s.setTheme);
  const toggleSidebar = useStore((s) => s.toggleSidebar);
  const outlineOpen = useStore((s) => s.outlineOpen);
  const toggleOutline = useStore((s) => s.toggleOutline);
  const vaultMode = useStore((s) => s.vaultMode);
  const vaultPanel = useStore((s) => s.vaultPanel);
  const setVaultPanel = useStore((s) => s.setVaultPanel);
  const setPaletteOpen = useStore((s) => s.setPaletteOpen);
  const saveState = useStore((s) => s.saveState);
  const isNarrow = useStore((s) => s.isNarrow);
  const user = useStore((s) => s.user);
  const { exportAs } = useExport();
  const { share, busy: sharing } = useShare();
  const t = useT();
  const [exportMenu, setExportMenu] = useState<MenuAnchor | null>(null);
  const [overflowMenu, setOverflowMenu] = useState<MenuAnchor | null>(null);

  const themeLabel = `${t('toolbar.cycleTheme')} — ${t(THEME_LABEL_KEY[theme])}`;

  return (
    <header className="mb-topbar-wrap">
      <div className="mb-topbar">
        <button
          className="mb-btn mb-btn-mark"
          onClick={toggleSidebar}
          title={t('toolbar.toggleSidebar')}
          aria-label={t('toolbar.toggleSidebar')}
          dangerouslySetInnerHTML={{ __html: markMono }}
        />

        <button
          className="mb-btn mb-btn-icon mb-hide-narrow"
          aria-pressed={outlineOpen}
          onClick={toggleOutline}
          title={t('toolbar.toggleOutline')}
          aria-label={t('toolbar.toggleOutline')}
        >
          <Icon name="outline" />
        </button>

        {/* Only in vault mode: the button opens the knowledge-base panels, and
            outside that mode markbird stays a plain editor. */}
        {vaultMode && (
          <button
            className="mb-btn mb-btn-icon mb-hide-narrow"
            aria-pressed={vaultPanel !== null}
            onClick={() => setVaultPanel(vaultPanel ? null : 'tags')}
            title={t('vault.title')}
            aria-label={t('vault.title')}
          >
            <Icon name="tag" />
          </button>
        )}

        <input
          className="mb-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label={t('toolbar.docTitle')}
          spellCheck={false}
        />
        <span className={`mb-savestate mb-savestate--${saveState} mb-hide-narrow`}>
          {t(`save.${saveState}`)}
        </span>

        {viewMode === 'switch' && !isNarrow && (
          <div className="mb-switch-tabs" role="group" aria-label={t('toolbar.pane')}>
            <button aria-pressed={switchPane === 'source'} onClick={() => setSwitchPane('source')}>
              {t('toolbar.source')}
            </button>
            <button
              aria-pressed={switchPane === 'preview'}
              onClick={() => setSwitchPane('preview')}
            >
              {t('toolbar.preview')}
            </button>
          </div>
        )}

        {!isNarrow && (
          <button
            className="mb-btn"
            aria-pressed={viewMode === 'split'}
            onClick={toggleViewMode}
            title={t('toolbar.toggleView')}
          >
            {viewMode === 'split' ? t('toolbar.split') : t('toolbar.switch')}
          </button>
        )}

        <button
          className="mb-btn mb-btn-icon mb-hide-narrow"
          onClick={() => setTheme(THEME_CYCLE[theme])}
          title={themeLabel}
          aria-label={themeLabel}
        >
          <Icon name={THEME_ICON[theme]} />
        </button>

        <button
          className="mb-btn mb-hide-narrow"
          onClick={(e) => {
            const at = anchorBelow(e.currentTarget);
            setExportMenu((m) => (m ? null : at));
          }}
          aria-haspopup="menu"
          aria-expanded={!!exportMenu}
        >
          {t('toolbar.export')}
          <Icon name="chevron-down" size={14} />
        </button>

        <div className="mb-share-split">
          <button
            className="mb-btn mb-btn-primary"
            onClick={() => void share()}
            disabled={sharing}
            title={t('toolbar.shareTitle')}
          >
            {sharing ? t('toolbar.sharing') : t('toolbar.share')}
          </button>
          <button
            className="mb-btn mb-btn-primary mb-share-split-more"
            onClick={() => useStore.getState().setShareOptionsOpen(true)}
            disabled={sharing}
            title={t('toolbar.shareOptions')}
            aria-label={t('toolbar.shareOptions')}
          >
            <Icon name="chevron-down" size={14} />
          </button>
        </div>

        {!isNarrow && <AccountButton />}

        <button
          className="mb-btn mb-btn-icon mb-hide-narrow"
          onClick={() => useStore.getState().setSettingsOpen(true)}
          title={t('toolbar.settings')}
          aria-label={t('toolbar.settings')}
        >
          <Icon name="settings" />
        </button>

        <button
          className="mb-btn mb-btn-icon"
          onClick={() => setPaletteOpen(true)}
          title={t('toolbar.palette')}
          aria-label={t('toolbar.palette')}
        >
          <Icon name="command" />
        </button>

        {isNarrow && (
          <button
            className="mb-btn mb-btn-icon mb-overflow-menu"
            onClick={(e) => {
              const at = anchorBelow(e.currentTarget);
              setOverflowMenu((m) => (m ? null : at));
            }}
            title={t('toolbar.more')}
            aria-label={t('toolbar.more')}
            aria-haspopup="menu"
            aria-expanded={!!overflowMenu}
          >
            <Icon name="more-horizontal" />
          </button>
        )}
      </div>

      {isNarrow && (
        <div className="mb-topbar-tabs">
          <div className="mb-switch-tabs" role="group" aria-label={t('toolbar.pane')}>
            <button aria-pressed={switchPane === 'source'} onClick={() => setSwitchPane('source')}>
              {t('toolbar.source')}
            </button>
            <button
              aria-pressed={switchPane === 'preview'}
              onClick={() => setSwitchPane('preview')}
            >
              {t('toolbar.preview')}
            </button>
          </div>
        </div>
      )}

      {exportMenu && (
        <Menu anchor={exportMenu} onClose={() => setExportMenu(null)} label={t('toolbar.export')}>
          {EXPORT_ITEMS.map((e) => (
            <MenuItem key={e.fmt} onSelect={() => void exportAs(e.fmt)}>
              <Icon name="download" size={15} />
              {t(e.key)}
            </MenuItem>
          ))}
        </Menu>
      )}

      {overflowMenu && (
        <Menu
          anchor={overflowMenu}
          onClose={() => setOverflowMenu(null)}
          label={t('toolbar.more')}
          className="mb-overflow-list"
        >
          {!user ? (
            <MenuItem onSelect={() => useStore.getState().setAuthOpen(true)}>
              {t('acct.signIn')}
            </MenuItem>
          ) : (
            <>
              <MenuItem onSelect={() => useStore.getState().setSharesOpen(true)}>
                {t('acct.manageShares')}
              </MenuItem>
              <MenuItem onSelect={() => useStore.getState().setAccountDialogOpen(true)}>
                {t('acct.accountData')}
              </MenuItem>
            </>
          )}
          <MenuItem onSelect={() => useStore.getState().setShareOptionsOpen(true)}>
            <Icon name="share" size={15} />
            {t('toolbar.shareOptions')}
          </MenuItem>
          <MenuSeparator />
          {EXPORT_ITEMS.map((e) => (
            <MenuItem key={e.fmt} onSelect={() => void exportAs(e.fmt)}>
              <Icon name="download" size={15} />
              {t(e.key)}
            </MenuItem>
          ))}
          <MenuSeparator />
          <MenuItem onSelect={() => setTheme(THEME_CYCLE[theme])}>
            <Icon name={THEME_ICON[theme]} />
            {t(THEME_LABEL_KEY[theme])}
          </MenuItem>
          <MenuItem onSelect={toggleOutline}>
            <Icon name="outline" size={15} />
            {t('toolbar.toggleOutline')}
          </MenuItem>
          <MenuItem onSelect={() => useStore.getState().setSettingsOpen(true)}>
            <Icon name="settings" size={15} />
            {t('toolbar.settings')}
          </MenuItem>
        </Menu>
      )}
    </header>
  );
}
